import {
  reactQueryDelete,
  reactQueryGet,
  reactQueryPost,
  reactQueryPut,
} from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { UseMutationOptions, UseQueryOptions } from "react-query";
import { useMutation, useQuery } from "react-query";

import { t } from "@/i18n-js/instance";
import { useToast } from "@/react/components/SettingsApp/CompassCompat/Toast";
import type { ApiError } from "@/react/config/CustomErrors";

export interface PollPayload {
  allow_members_to_view_results: boolean;
  closes_at?: string;
  poll_options: {
    id?: number;
    value: string;
  }[];
  title: string;
}

export interface PollOptionType {
  id: number;
  value: string;
}

export interface PollResults {
  detailed_results?: {
    responses: number;
    percentage: number;
    poll_option_id: number;
  }[];
  total_responses: number;
}

export interface PollResponse {
  allow_members_to_view_results: boolean;
  closes_at?: string;
  community_member_id: number;
  community_member_response_option_id?: number;
  created_at: string;
  id: number;
  poll_options: PollOptionType[];
  results?: PollResults;
  sgid: string;
  status: "draft" | "active" | "inactive" | "closed";
  title: string;
  updated_at: string;
}

export const useCreatePoll = (
  options: UseMutationOptions<PollResponse, ApiError, PollPayload> = {},
) =>
  useMutation({
    mutationFn: poll =>
      reactQueryPost<PollResponse>(internalApi.polls.create(), poll),
    ...options,
  });

interface EditPollPayload extends PollPayload {
  id: number;
}

export const useEditPoll = (
  options: UseMutationOptions<PollResponse, ApiError, EditPollPayload> = {},
) =>
  useMutation({
    mutationFn: poll => reactQueryPut(internalApi.polls.update(poll.id), poll),
    ...options,
  });

export const pollKeys = {
  detail: (sgid?: string) => ["poll", sgid] as const,
  voteList: (pollId: number, pollOptionId: number) =>
    ["poll-vote-list", pollId, pollOptionId] as const,
};

export interface UsePollProps {
  options?: Omit<UseQueryOptions<PollResponse>, "queryKey">;
  sgid?: string;
}

export const usePoll = ({ sgid, options = {} }: UsePollProps) =>
  useQuery<PollResponse>({
    queryKey: pollKeys.detail(sgid),
    queryFn: () => {
      if (!sgid) {
        throw new Error("sgid is required");
      }
      return reactQueryGet(internalApi.polls.showBySgid(sgid));
    },
    enabled: !!sgid,
    ...options,
  });

export const useUndoMutation = ({
  options = {},
}: {
  options?: UseMutationOptions<any, unknown, any>;
} = {}) =>
  useMutation({
    mutationFn: (pollId: number) =>
      reactQueryDelete(internalApi.polls.undo(pollId)),
    ...options,
  });

export const useExportPollResponses = () => {
  const toast = useToast();

  const { mutate: handleExportPollResponses, isPending: isExporting } =
    useMutation<{ success: boolean; email?: string }, ApiError, number>({
      mutationFn: (pollId: number) =>
        reactQueryPost(internalApi.polls.exportCsv(pollId)),
      onSuccess: data => {
        toast.success(t("export_member_csv.message", { email: data.email }));
      },
      onError: error => {
        toast.error(error.message);
      },
    });

  return { handleExportPollResponses, isExporting };
};
